import {
  Application,
  Assets,
  Container,
  Sprite,
  Spritesheet,
  AnimatedSprite,
} from "pixi.js";
import { gsap } from "gsap";
import { PixiPlugin } from "gsap/PixiPlugin";

gsap.registerPlugin(PixiPlugin);

const app = new Application();
const container = new Container();
let classRoomDataFile = "";
let classRoomHeight = 0;
let classRoomWidth = 0;
let dragStartOffset = 0;
let dragStartX = 0;
const canvas = document.getElementById("mainCanvas");
const zoom = document.getElementById("lookingGlass");
const assetPath = "/wp-content/themes/kopiraittilankoulu/assets";

// custom sprites
let chest = new Container();
let lamp1 = new Container();
let lamp2 = new Container();
let lamp3 = new Container();

let checkContainer = new Container();
let taskCheck = [];

let fortunewheelContainer = new Container();
let fortunewheel = new Sprite();

window.initClassRoom = function (fileName) {
  classRoomDataFile = fileName;
  gsap.to(container, { alpha: 0, duration: 0.2, onComplete: loadAssets });
};

(async () => {
  await app.init({
    backgroundAlpha: 0,
    resizeTo: window,
    canvas: canvas,
    antialias: true,
  });

  PixiPlugin.registerPIXI(app);

  container.height = app.screen.height;
  app.stage.addChild(container);

  app.stage.eventMode = "static";
  app.stage.hitArea = app.screen;
  app.stage.on("pointerup", onDragEnd);
  app.stage.on("pointerupoutside", onDragEnd);

  // Listen for window resize events
  window.addEventListener("resize", resize);
})();

async function loadAssets() {
  let assetList = "";

  await fetch(assetPath + classRoomDataFile)
    .then((response) => response.json())
    .then((json) => (assetList = json));

  container.alpha = 0;
  container.removeChildren();

  await assetList.forEach((asset) => {
    if (asset.aste == undefined || asset.aste.indexOf(aste) !== -1) {
      loadAsset(asset);
    }
  });

  gsap.to(container, {
    alpha: 1,
    duration: 0.5,
    delay: 0.5,
    ease: "power2.out",
    onComplete: classRoomLoaded,
  });
}

async function loadAsset(asset) {
  const texture = await Assets.load(assetPath + asset.src).then((texture) => {
    const sprite = new Sprite(texture);
    sprite.data = asset;

    switch (asset.itemtype) {
      case "bg":
        initBackground(sprite);
        break;
      case "deco":
        initImage(sprite, asset);
        break;
      case "animation":
        initAnimation(texture, asset);
        break;
      case "button":
        initButton(sprite, asset);
        break;
      case "whiteboard":
        initWhiteBoard(texture);
        break;
      case "lamp":
        initLamp(texture, asset);
        break;
      case "chest":
        initChest(texture, asset);
        break;
      case "findcopyright":
        initFindC(texture);
        break;
      case "wheeloffortune":
        initWheelOfFortune(sprite, asset);
        break;
      case "popup":
        break;

      default:
    }
  });
}

async function loadChildAsset(src, parentSprite) {
  const texture = await Assets.load(assetPath + src).then((texture) => {
    const childSprite = new Sprite(texture);
    parentSprite.addChild(childSprite);
  });
}

function initBackground(sprite) {
  classRoomHeight = sprite.texture.height;
  classRoomWidth = sprite.texture.width;
  sprite.zIndex = -1;
  sprite.eventMode = "static";
  sprite.cursor = "grab";
  sprite.on("pointerdown", onDragStart);
  container.addChild(sprite);
  resize();
}

function initImage(sprite, asset) {
  if (asset.x != undefined && asset.y != undefined) {
    sprite.x = asset.x;
    sprite.y = asset.y;
  }
  sprite.zIndex = 2;
  sprite.alpha = asset.opacity != undefined ? asset.opacity : 1;
  container.addChild(sprite);
}

async function initAnimation(texture, asset) {
  const spritesheet = new Spritesheet(texture, asset.atlas);
  await spritesheet.parse();
  const anim = new AnimatedSprite(spritesheet.animations.default);
  anim.data = asset;
  anim.animationSpeed = asset.speed;
  anim.loop = false;

  if (asset.loop === true) {
    anim.loop = true;
    anim.play();
  }

  if (asset.action) {
    initButton(anim, asset);
  } else {
    anim.x = asset.x;
    anim.y = asset.y;
    container.addChild(anim);
  }

  anim.onComplete = function () {};
}

async function initButton(sprite, asset) {
  if (asset.x != undefined && asset.y != undefined) {
    sprite.x = asset.x;
    sprite.y = asset.y;
  }
  sprite.zIndex = 2;
  sprite.eventMode = "static";
  sprite.cursor = "pointer";
  sprite.on("pointerdown", onItemClick);
  sprite.alpha = asset.opacity != undefined ? asset.opacity : 1;

  switch (asset.tween) {
    case "wave":
      sprite.anchor.set(0.5);
      sprite.x += sprite.width / 2;
      sprite.y += sprite.height / 2;
      gsap.to(sprite, {
        keyframes: [
          { rotation: -0.1, duration: 0.2, ease: "power1.out" },
          { rotation: 0.1, duration: 0.2, ease: "power1.out" },
          { rotation: 0, duration: 0.2, ease: "power1.out" },
        ],
        delay: Math.random() * 3,
        repeat: -1,
        repeatDelay: 3.0,
      });
      break;
  }

  container.addChild(sprite);
}

function resize() {
  const scaleY = app.screen.height / classRoomHeight;
  container.scale.set(scaleY);
  container.x = (app.screen.width - classRoomWidth * scaleY) / 2;
}

function onDragStart(event) {
  if (container.width < app.screen.width) {
    return;
  }
  dragStartOffset = event.global.x;
  dragStartX = container.x;
  app.stage.on("pointermove", onDragMove);
}

function onDragMove(event) {
  container.x = dragStartX - (dragStartOffset - event.global.x);
  if (container.x < app.screen.width - container.width) {
    container.x = app.screen.width - container.width;
  }
  if (container.x > 0) {
    container.x = 0;
  }
}

function onDragEnd() {
  app.stage.off("pointermove", onDragMove);
}

function onItemClick(event) {
  const assetData = event.target.data;

  // play sound effect
  if (assetData.sound && event.target.alpha > 0) {
    soundEffect(assetData.sound);
  }

  // play animation
  if (assetData.action == "play") {
    event.target.gotoAndPlay(0);
    console.debug(event.target);
  }

  // route to modal manager
  if (assetData.action == "modal") {
    modalManager.openTask(assetData.app, assetData.url);
  }

  // route to page
  if (assetData.action == "page") {
    window.open(assetData.url, "_blank");
  }

  // open native popup - some legacy games use this
  if (assetData.action == "legacymodal") {
    openPopUp(assetData.app);
  }

  // open magnific popup - some legacy games use this
  if (assetData.action == "magnific") {
    openMagnificPopUp(assetData.app);
  }

  // open magnific popup - some legacy games use this
  if (assetData.action == "task") {
    openTask(assetData.app, assetData.index);
  }

  if (assetData.action == "test") {
    openTest(assetData.app, assetData.level);
  }

  // open jQuest
   if (assetData.action == "jquest") {
      console.debug("open jQuest: " + assetData.app);
      window.jquest.openPopup(assetData.app);
  }

  // open door
  if (assetData.action == "door") {
    openDoor(assetData.roomnum);
  }

  // trigger tween
  switch (assetData.tween) {
    case "up":
      gsap.to(event.target, {
        y: -event.target.height - 100,
        duration: 2.0,
        ease: "power2.in",
      });
      break;
    case "vanish":
      gsap.to(event.target, { alpha: 0, duration: 0.1, ease: "power2.in" });
      break;
  }

  // console.debug("Clicked on: ", event.target.data);
}

// Item specific functions
async function initWhiteBoard(texture) {
  taskCheck = [];
  checkContainer.x = 2460;
  checkContainer.y = 205;

  for (let i = 0; i < 6; i++) {
    const checkitem = new Sprite(texture);
    checkitem.alpha = 0; // initially hidden
    checkitem.y = i * 98;
    checkContainer.addChild(checkitem);
    taskCheck.push(checkitem);
  }

  for (let i = 0; i < 4; i++) {
    const checkitem = new Sprite(texture);
    checkitem.alpha = 0; // initially hidden
    checkitem.x = 780;
    checkitem.y = i * 98;
    checkContainer.addChild(checkitem);
    taskCheck.push(checkitem);
  }

  container.addChild(checkContainer);
}

window.checkTask = function () {
  if (currentTaskIndex < taskCheck.length) {
    const checkitem = taskCheck[currentTaskIndex - 1];
    checkitem.alpha = 1; // show the checkmark
  }
};

async function initFindC(texture) {
  hiddenObjects = new Container();
  hiddenObjects.zIndex = 3;

  visibleObjects = new Container();
  visibleObjects.zIndex = 3;

  let places = [
    { x: 5604, y: 732, name: "Juliste" },
    { x: 6310, y: 706, name: "Kartta" },
    { x: 1298, y: 812, name: "Nuotti" },
    { x: 4530, y: 620, name: "Patsas" },
    { x: 1130, y: 1345, name: "Sarjakuva" },
    { x: 4024, y: 1098, name: "Dvd" },
    { x: 3580, y: 920, name: "Tietokonepeli" },
    { x: 4254, y: 596, name: "Radio" },
    { x: 4386, y: 1052, name: "TV" },
    { x: 4066, y: 1280, name: "CD-levy" },
    { x: 3392, y: 1052, name: "Sanomalehti" },
    { x: 6005, y: 226, name: "Kirja" },
    { x: 2332, y: 526, name: "Taulu" },
    { x: 602, y: 488, name: "Valokuva" },
    { x: 4288, y: 214, name: "Piirrustus" },
  ];

  for (let i = 0; i < places.length; i++) {
    const c = new Sprite(texture);
    c.x = places[i].x;
    c.y = places[i].y;
    c.label = places[i].name;
    c.cursor = "pointer";
    c.eventMode = "static";
    c.on("pointerdown", onCopyrightClick);
    hiddenObjects.addChild(c);
  }
}

window.magnify = function (m) {
  magnifier = m;

  if (m) {
    app.stage.on("pointermove", drawLookingGlass);
    // if (is_touch_device()) {}

    hiddenObjects.alpha = 0.05;

    container.addChild(hiddenObjects);
    container.addChild(visibleObjects);
  } else {
    zoom.style.display = "none";
    app.stage.off("pointermove", drawLookingGlass);
    // if (is_touch_device()) {}

    container.removeChild(hiddenObjects);
    container.removeChild(visibleObjects);
  }
};

function drawLookingGlass(e) {
  var zoomCtx = zoom.getContext("2d");
  var x = e.x;
  var y = e.y;

  if (!x) {
    x = e.stageX;
    y = e.stageY;
  }

  // reveal hidden objects, force immidiate render
  hiddenObjects.alpha = 1;
  app.render();

  // draw copy to zoom canvas
  zoomCtx.drawImage(canvas, x - 50, y - 50, 100, 100, 0, 0, 200, 200);

  // hide objects, force immidiate render
  hiddenObjects.alpha = 0.05;
  app.render();

  zoom.style.top = y - 100 + "px";
  zoom.style.left = x - 100 + "px";
  zoom.style.display = "block";
}

// find-c game found item click handler
function onCopyrightClick(e) {
  const itemname = e.target.label;
  // console.debug("found " + itemname);
  if (foundObjects.indexOf(itemname) < 0) {
    foundObjects.push(itemname);
    $("#scorevalue").html(foundObjects.length + " / 15");
    soundEffect("found");
    e.target.alpha = 0.4; // used to be image "c-green"
    drawLookingGlass(e);
    if (foundObjects.length == 15) {
      endFindGame();
    }
  }
}

async function initWheelOfFortune(sprite, asset) {
  sprite.eventMode = "static";
  sprite.cursor = "pointer";
  sprite.on("pointerdown", onWheelClick);
  sprite.x = asset.x;
  sprite.y = asset.y;
  sprite.zIndex = 2;
  sprite.scale.set(0.4);
  sprite.anchor.set(0.5);
  sprite.alpha = asset.opacity != undefined ? asset.opacity : 1;
  fortunewheel = sprite;
  fortunewheelContainer.addChild(fortunewheel);

  // lives
  lifesymbol1 = new Sprite(await Assets.load(assetPath + asset.heart));
  lifesymbol2 = new Sprite(await Assets.load(assetPath + asset.heart));
  lifesymbol3 = new Sprite(await Assets.load(assetPath + asset.heart));

  container.addChild(fortunewheelContainer);
}

function onWheelClick(event) {
  startWheelOfFortune();
}

window.spinWheel = function (sector) {
  gsap.to(fortunewheel, {
    rotation: fortunewheel.rotation + 4 * 360 + sector,
    duration: 5,
    ease: "power2.out",
    onComplete: wheelDone,
  });
};

async function initLamp(texture, asset) {
  const lamp = new Container();
  container.addChild(lamp);

  lamp.data = asset;
  lamp.x = asset.x;
  lamp.y = asset.y;
  lamp.zIndex = 2;
  lamp.eventMode = "static";
  lamp.cursor = "pointer";
  lamp.on("pointerdown", onItemClick);

  const sprite = new Sprite(texture);
  lamp.addChild(sprite);

  // load lamp on texture
  await loadChildAsset(asset.on, lamp).then(() => {
    if (asset.id == "lamppu1") {
      lamp1 = lamp.getChildAt(1);
      lamp1.visible = false;
    }
    if (asset.id == "lamppu2") {
      lamp2 = lamp.getChildAt(1);
      lamp2.visible = false;
    }
    if (asset.id == "lamppu3") {
      lamp3 = lamp.getChildAt(1);
      lamp3.visible = false;
    }
  });
}

async function initChest(texture, asset) {
  chest = new Container();
  container.addChild(chest);

  chest.data = asset;
  chest.x = asset.x;
  chest.y = asset.y;
  chest.zIndex = 2;
  chest.eventMode = "static";
  chest.cursor = "pointer";
  chest.on("pointerdown", onItemClick);

  const sprite = new Sprite(texture);
  chest.addChild(sprite);

  // lock closed
  await loadChildAsset(asset.lockclosed, chest).then(() => {
    const lock1 = chest.getChildAt(1);
    lock1.x = 30;
    lock1.y = 100;

    const lock2 = new Sprite(lock1.texture);
    chest.addChild(lock2);
    lock2.x = 70;
    lock2.y = 100;

    const lock3 = new Sprite(lock1.texture);
    chest.addChild(lock3);
    lock3.x = 110;
    lock3.y = 100;
  });

  // lock open
  await loadChildAsset(asset.lockopen, chest).then(() => {
    const lock1 = chest.getChildAt(4);
    lock1.x = 30;
    lock1.y = 100;
    lock1.visible = false;

    const lock2 = new Sprite(lock1.texture);
    chest.addChild(lock2);
    lock2.x = 70;
    lock2.y = 100;
    lock2.visible = false;

    const lock3 = new Sprite(lock1.texture);
    chest.addChild(lock3);
    lock3.x = 110;
    lock3.y = 100;
    lock3.visible = false;
  });
}

function openLock(num) {
  chest.getChildAt(num).visible = false;
  chest.getChildAt(num + 3).visible = true;
}

window.unlockGame = function (num) {
  switch (num) {
    case 1:
      lamp2.visible = true;
      openLock(1);
      break;
    case 2:
      lamp3.visible = true;
      openLock(2);
      break;
    case 3:
      lamp1.visible = true;
      openLock(3);
      break;
  }
};
